import { SvgIconComponent } from "@mui/icons-material";
import { Box, ButtonBase, Stack, Theme, Typography } from "@mui/material";
import { green, grey, red, yellow } from "@mui/material/colors";
import { useEffect, useState } from "react";

type KeyColor = "green" | "neutral" | "red" | "yellow";

type KeyProps = {
  IconOrChar: SvgIconComponent | string;
  color?: KeyColor;
  label: string;
  onClick: () => void;
  pressed?: boolean;
};

const keySidesColors = {
  green: (theme: Theme) =>
    theme.palette.mode === "dark" ? green[900] : green[700],
  neutral: (theme: Theme) =>
    theme.palette.mode === "dark" ? grey[700] : grey[400],
  red: (theme: Theme) => (theme.palette.mode === "dark" ? red[900] : red[700]),
  yellow: (theme: Theme) =>
    theme.palette.mode === "dark" ? yellow[900] : yellow[700],
};

const keyFaceColors = {
  green: (theme: Theme) =>
    theme.palette.mode === "dark" ? green[700] : green[400],
  neutral: (theme: Theme) =>
    theme.palette.mode === "dark" ? grey[500] : grey[300],
  red: (theme: Theme) => (theme.palette.mode === "dark" ? red[700] : red[400]),
  yellow: (theme: Theme) =>
    theme.palette.mode === "dark" ? yellow[700] : yellow[600],
};

function Key({
  IconOrChar,
  color = "neutral",
  label,
  onClick,
  pressed = false,
}: KeyProps) {
  return (
    <ButtonBase disableRipple onClick={onClick}>
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: keySidesColors[color],
          transform: pressed ? "scale(0.95)" : undefined,
        }}
      >
        <Stack
          justifyContent={"flex-end"}
          alignItems={"center"}
          gap={0}
          sx={{
            width: 72,
            height: 72,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
            m: "auto",
            bgcolor: keyFaceColors[color],
          }}
        >
          <Stack
            justifyContent={"center"}
            alignItems={"center"}
            sx={{ width: 24, height: 24 }}
            color={"common.black"}
          >
            {typeof IconOrChar === "string" ? IconOrChar : <IconOrChar />}
          </Stack>
          <Typography gutterBottom variant="body2" color="common.black">
            {label}
          </Typography>
        </Stack>
      </Box>
    </ButtonBase>
  );
}

const ACCEPT = "KeyD";
const REJECT = "KeyA";
const PREV = "KeyW";
const NEXT = "KeyS";
const TOGGLE_CHARACTER = "KeyC";

type Props = {
  disabled?: boolean;
  onAccept: () => void;
  showNextPrev?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  onReject: () => void;
  showCharacterToggle?: boolean;
  onToggleCharacter?: () => void;
};

export function ValidationControls({
  disabled,
  onAccept,
  showNextPrev = false,
  onNext,
  onPrev,
  onReject,
  showCharacterToggle = false,
  onToggleCharacter,
}: Props) {
  const [pressedKey, setPressedKey] = useState<string>();

  useEffect(() => {
    if (disabled) {
      return;
    }

    function handleKeyUp(e: KeyboardEvent) {
      if (e.code === pressedKey) {
        setPressedKey(undefined);
        if (e.code === ACCEPT) onAccept();
        if (e.code === REJECT) onReject();
        if (showNextPrev) {
          if (e.code === PREV && onPrev) onPrev();
          if (e.code === NEXT && onNext) onNext();
        }
        if (
          showCharacterToggle &&
          e.code === TOGGLE_CHARACTER &&
          onToggleCharacter
        )
          onToggleCharacter();
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if ([ACCEPT, REJECT, PREV, NEXT, TOGGLE_CHARACTER].includes(e.code)) {
        setPressedKey(e.code);
      }
    }

    function handleMouseLeave() {
      setPressedKey(undefined);
    }

    // event "keypress" is not triggered for arrow keys
    window.document.addEventListener("keyup", handleKeyUp);
    window.document.addEventListener("keydown", handleKeyDown);
    window.document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.document.removeEventListener("keyup", handleKeyUp);
      window.document.removeEventListener("keydown", handleKeyDown);
      window.document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [
    disabled,
    onAccept,
    showNextPrev,
    onNext,
    onPrev,
    onReject,
    showCharacterToggle,
    onToggleCharacter,
    pressedKey,
  ]);

  const clickKey = (key: string) => {
    if (disabled) {
      return;
    }
    setPressedKey(key);
    setTimeout(() => {
      setPressedKey(undefined);
    }, 50);
    if (key === ACCEPT) onAccept();
    if (key === REJECT) onReject();
    if (showNextPrev) {
      if (key === PREV && onPrev) onPrev();
      if (key === NEXT && onNext) onNext();
    }
    if (showCharacterToggle && key === TOGGLE_CHARACTER && onToggleCharacter)
      onToggleCharacter();
  };

  return (
    <Stack
      sx={{ position: "fixed", bottom: 0, left: 0, right: 0, pt: 2, pb: 1 }}
      direction={"row"}
      gap={0.5}
      justifyContent={"center"}
    >
      <Key
        IconOrChar={"A"}
        color={"red"}
        label={"Reject"}
        onClick={() => {
          clickKey(REJECT);
        }}
        pressed={pressedKey === REJECT}
      />
      {showNextPrev && (
        <>
          <Key
            IconOrChar={"W"}
            label={"Prev"}
            onClick={() => {
              clickKey(PREV);
            }}
            pressed={pressedKey === PREV}
          />
          <Key
            IconOrChar={"S"}
            label={"Next"}
            onClick={() => {
              clickKey(NEXT);
            }}
            pressed={pressedKey === NEXT}
          />
        </>
      )}
      <Key
        IconOrChar={"D"}
        color={"green"}
        label={"Accept"}
        onClick={() => {
          clickKey(ACCEPT);
        }}
        pressed={pressedKey === ACCEPT}
      />
      {showCharacterToggle && (
        <Key
          IconOrChar={"C"}
          color={"yellow"}
          label={"Character"}
          onClick={() => {
            clickKey(TOGGLE_CHARACTER);
          }}
          pressed={pressedKey === TOGGLE_CHARACTER}
        />
      )}
    </Stack>
  );
}
